import type { FC, ReactNode } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import type { Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { GroupedMetrics, RevenueEntry } from '../types';
import { visuallyHidden } from './RevenueDetailTable.styles';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    minWidth: 0,
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  paper: {
    minWidth: 0,
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5),
    },
  },
  header: {
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
  },
  title: {
    color: '#000000',
    fontWeight: 500,
    overflowWrap: 'anywhere' as const,
  },
  subtitle: {
    marginTop: theme.spacing(0.5),
    color: '#000000',
    overflowWrap: 'anywhere' as const,
  },
  equationRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.2fr) auto minmax(0, 1fr) auto minmax(0, 1fr)',
    gap: theme.spacing(1),
    alignItems: 'center',
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: `${theme.spacing(2)} minmax(0, 1fr)`,
      columnGap: theme.spacing(0.5),
      rowGap: theme.spacing(0.5),
      alignItems: 'baseline',
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
  },
  equationCell: {
    display: 'flex',
    minWidth: 0,
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      gridColumn: '2',
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'baseline',
      columnGap: theme.spacing(1),
    },
  },
  equationResult: {
    [theme.breakpoints.down('sm')]: {
      gridColumn: '1 / -1',
    },
  },
  cellLabel: {
    marginBottom: theme.spacing(0.25),
    color: '#000000',
    overflowWrap: 'anywhere' as const,
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 auto',
      minWidth: 0,
      marginBottom: 0,
    },
  },
  cellLabelResult: {
    [theme.breakpoints.down('sm')]: {
      fontWeight: 600,
    },
  },
  cellValue: {
    color: theme.palette.primary.main,
    fontFamily: 'monospace',
    fontWeight: 500,
    textAlign: 'right' as const,
    overflowWrap: 'anywhere' as const,
    wordBreak: 'break-word' as const,
    [theme.breakpoints.down('sm')]: {
      flex: '0 0 auto',
      marginLeft: 'auto',
    },
  },
  cellValueUnavailable: {
    color: '#000000',
  },
  cellNote: {
    marginTop: theme.spacing(0.5),
    color: '#000000',
    fontStyle: 'italic',
    overflowWrap: 'anywhere' as const,
    [theme.breakpoints.down('sm')]: {
      flexBasis: '100%',
      marginTop: 0,
    },
  },
  operator: {
    color: '#000000',
    fontWeight: 700,
    textAlign: 'center' as const,
    userSelect: 'none' as const,
    [theme.breakpoints.down('sm')]: {
      gridColumn: '1',
    },
  },
  operatorLabel: visuallyHidden(theme),
}));

type EquationDef = {
  readonly leftCode: string;
  readonly leftLabelKey: string;
  readonly operator: '+' | '−';
  readonly rightLabel1Key: string;
  readonly rightCode1: string;
  readonly rightLabel2Key: string;
  readonly rightCode2: string;
};

const EQUATIONS: readonly EquationDef[] = [
  {
    leftLabelKey: 'equations.revenueInvoicedLabel',
    leftCode: 'revenue_invoiced',
    operator: '+',
    rightLabel1Key: 'equations.revenueCollectedLabel',
    rightCode1: 'revenue_collected',
    rightLabel2Key: 'equations.revenuePendingLabel',
    rightCode2: 'revenue_pending',
  },
  {
    leftLabelKey: 'equations.costsIncurredLabel',
    leftCode: 'costs_incurred',
    operator: '+',
    rightLabel1Key: 'equations.costsSettledLabel',
    rightCode1: 'costs_settled',
    rightLabel2Key: 'equations.costsOpenLabel',
    rightCode2: 'costs_open',
  },
  {
    leftLabelKey: 'equations.netProfitLabel',
    leftCode: 'profit_net',
    operator: '−',
    rightLabel1Key: 'equations.revenueCollectedLabel',
    rightCode1: 'revenue_collected',
    rightLabel2Key: 'equations.costsSettledLabel',
    rightCode2: 'costs_settled',
  },
  {
    leftLabelKey: 'equations.currentAssetsLabel',
    leftCode: 'assets_current',
    operator: '+',
    rightLabel1Key: 'equations.revenueCollectedLabel',
    rightCode1: 'revenue_collected',
    rightLabel2Key: 'equations.revenuePendingLabel',
    rightCode2: 'revenue_pending',
  },
];

const findEntry = (grouped: GroupedMetrics, code: string): RevenueEntry | undefined => (
  [...grouped.base, ...grouped.derived, ...grouped.executive].find((entry) => entry.code === code)
);

type RevenueReconciliationProps = {
  readonly grouped: GroupedMetrics;
};

const RevenueReconciliation: FC<RevenueReconciliationProps> = ({ grouped }) => {
  const classes = useStyles();
  const { t } = useTranslation('revenue');

  const getEntryNote = (entry: RevenueEntry | undefined): string => {
    if (!entry || !entry.available) {
      return t('reconciliation.unavailable', 'Not available for this order');
    }

    if (!entry.hasBasis && entry.basisNote) {
      return t('kpi.basisNote', entry.basisNote);
    }

    return '';
  };

  const renderEquationCell = (
    label: string,
    entry: RevenueEntry | undefined,
    isResult = false
  ): ReactNode => (
    <Box className={`${classes.equationCell}${isResult ? ` ${classes.equationResult}` : ''}`}>
      <Typography
        variant="caption"
        className={`${classes.cellLabel}${isResult ? ` ${classes.cellLabelResult}` : ''}`}
      >
        {label}
      </Typography>
      <Typography
        className={`${classes.cellValue} ${!entry || !entry.available ? classes.cellValueUnavailable : ''}`}
      >
        {entry ? entry.display : '—'}
      </Typography>
      {getEntryNote(entry) && (
        <Typography variant="caption" className={classes.cellNote}>
          {getEntryNote(entry)}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box className={classes.container}>
      <Paper elevation={1} className={classes.paper}>
        <Box component="header" className={classes.header}>
          <Typography component="h2" variant="h6" className={classes.title}>
            {t('reconciliation.title', 'Reconciliation')}
          </Typography>
          <Typography variant="body2" className={classes.subtitle}>
            {t(
              'reconciliation.subtitle',
              'Reference equations clarify cash versus accrual values; nothing is recomputed here.'
            )}
          </Typography>
        </Box>

        {[...EQUATIONS].sort((a, b) =>
          a.leftLabelKey.localeCompare(b.leftLabelKey, undefined, { sensitivity: 'base' })
        ).map((equation) => {
          const left = findEntry(grouped, equation.leftCode);
          const rightOne = findEntry(grouped, equation.rightCode1);
          const rightTwo = findEntry(grouped, equation.rightCode2);

          return (
            <Box key={equation.leftCode} className={classes.equationRow}>
              {renderEquationCell(t(equation.leftLabelKey), left, true)}
              <Typography className={classes.operator} aria-hidden="true">=</Typography>
              <Typography className={classes.operatorLabel}>
                {t('reconciliation.equals', 'Equals')}
              </Typography>
              {renderEquationCell(t(equation.rightLabel1Key), rightOne)}
              <Typography className={classes.operator} aria-hidden="true">
                {equation.operator}
              </Typography>
              <Typography className={classes.operatorLabel}>
                {equation.operator === '+'
                  ? t('reconciliation.plus', 'Plus')
                  : t('reconciliation.minus', 'Minus')}
              </Typography>
              {renderEquationCell(t(equation.rightLabel2Key), rightTwo)}
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
};

export { RevenueReconciliation };
export default RevenueReconciliation;
